import React, { useState, useEffect, useRef } from "react";
import { Spinner } from "react-bootstrap";
import styles from "../../css/cashFlowTable.module.css";
import { getDisclosureOptions, getTemplateDetailsApiHandler, setDisclosureCellFormula, deleteDisclosureCellFormula ,
 getCashflowOptions,
 setCashflowCellFormula, setCashflow, deleteCashflow, deleteCashflowCellFormula,
 getPopulateFSDetailsApiHandler, setSocieCellFormula, deleteSocieCellFormula,
 getPopulateFSFormulaOptions,  setStandaloneCashflowWorkingsCellFormula,
 setStandaloneCashflowCellFormula, setStandaloneSocieCellFormula, setStandaloneDisclosureCellFormula,
 deleteStandaloneDisclosureCellFormula,
 deleteStandaloneCashflowWorkingsCellFormula,
 deleteStandaloneCashflowCellFormula,
 deleteStandaloneSocieCellFormula,
 getFormulaOptions,
 setConsolCashflowWorkingsCellFormula,
 deleteConsolCashflowWorkingsCellFormula,
 getConsolPopulateFSDetailsApiHandler,
 setConsolCashflowCellFormula,
 deleteConsolCashflowCellFormula,
 getConsolFormulaOptionsApiHandler,
 setConsolDisclosureCellFormula,
 deleteConsolDisclosureCellFormula
 } from "../../../WorkSpace/Components/ApiServices/apiHandler";
import { ReactComponent as DownArrow } from "../../assets/icons/down_arrow.svg";
import { ReactComponent as AddIcon } from "../../assets/icons/addEcl.svg";
import { ReactComponent as AddElementIcon } from "../../assets/icons/grayAddIcon.svg";
import Swal from "sweetalert";
import DeletePopup from "../../elements/DeletePopup";
import SuccessPopup from "../../elements/SuccessPopup";
import ConfirmPopup from "../../elements/ConfirmPopup";
import ErrorPopup from "../../elements/ErrorPopup";
import { Tooltip } from "../../elements/Tooltip";
import { ReactComponent as DeleteGreyIcon } from "../../assets/icons/deleteGrey.svg";
import { ReactComponent as DeleteIcon } from "../../assets/icons/delete_red.svg";
import { ReactComponent as RemoveIcon } from "../../assets/icons/close_grey.svg";
import { TbPlus, TbMinus, TbX, TbDivide } from "react-icons/tb";

const OPERATORS = ["＋", "－", "×", "÷"];

const OperatorIcon = ({ type, size = 18, strokeWidth = 2 }) => {
  switch(type) {
    case "＋":
    case "+": return <TbPlus size={size} strokeWidth={strokeWidth} />;
    case "－":
    case "-": return <TbMinus size={size} strokeWidth={strokeWidth} />;
    case "×":
    case "*": return <TbX size={size} strokeWidth={strokeWidth} />;
    case "÷":
    case "/": return <TbDivide size={size} strokeWidth={strokeWidth} />;
    default: return <TbPlus size={size} strokeWidth={strokeWidth} />;
  }
};

const OPERATOR_MAP = {
  "＋": "+",
  "－": "-",
  "×": "*",
  "÷": "/",
};

const isValidObjectId = (id) => typeof id === "string" && /^[0-9a-fA-F]{24}$/.test(id);

// ── Dropdown: pass label=undefined to hide label ─────────────────────────
const Dropdown = ({ label, placeholder, options, value, onChange, initiallyOpen = false, disabled = false }) => {
  const [open, setOpen] = useState(initiallyOpen);
  const [search, setSearch] = useState("");
  const ref = useRef();
  const inputRef = useRef();
  const activeItemRef = useRef(null);
  const menuRef = useRef(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const justSelected = useRef(false);

  const getOptValue = (opt) => opt && typeof opt === 'object' ? opt.value : opt;
  const getOptLabel = (opt) => opt && typeof opt === 'object' ? opt.label : opt;
  
  const currentLabel = value ? (getOptLabel(options.find(o => getOptValue(o) === value) || value)) : "";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (justSelected.current) {
        justSelected.current = false;
        return;
      }
      if (
        ref.current && 
        !ref.current.contains(event.target) &&
        document.contains(event.target)
      ) {
        setOpen(false);
        if (search === "") {
          // Only clear if the user explicitly blanked the input (not after a selection)
          // Do nothing — keep the existing value intact
        } else {
          setSearch(value || "");
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [search, value, onChange]);

  // Sync search with value when closed
  useEffect(() => {
    if (!open) setSearch(value || "");
  }, [value, open]);

  // Sync open state when initiallyOpen changes
  useEffect(() => {
    if (initiallyOpen) {
      setOpen(true);
    }
  }, [initiallyOpen]);

  // Scroll active item into view when opened
  useEffect(() => {
    if (open && activeItemRef.current) {
      activeItemRef.current.scrollIntoView({ block: "nearest", behavior: "auto" });
    }
  }, [open]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [search, open]);

  useEffect(() => {
    if (highlightedIndex >= 0 && menuRef.current) {
      const activeEl = menuRef.current.children[highlightedIndex];
      if (activeEl) {
        activeEl.scrollIntoView({
          block: "nearest",
        });
      }
    }
  }, [highlightedIndex]);

  // Auto-correct or clear invalid values when options update (e.g., from hierarchy rename)
  useEffect(() => {
    if (value && options && options.length > 0 && !options.some(o => getOptValue(o) === value)) {
      const valLower = value.toLowerCase();
      const match = options.find(
        (o) => getOptLabel(o).toLowerCase().includes(valLower) || valLower.includes(getOptLabel(o).toLowerCase())
      );
      if (match) {
        onChange && onChange(getOptValue(match));
      } else {
        onChange && onChange("");
      }
    }
  }, [value, options, onChange]);

  const filtered = options.filter((opt) => {
    const label = getOptLabel(opt);
    return label != null && String(label).toLowerCase().includes(search.toLowerCase());
  });

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter") {
        setOpen(true);
        setHighlightedIndex(0);
        e.preventDefault();
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => {
        if (prev < 0) return 0;
        const nextIndex = prev + 1;
        return nextIndex < filtered.length ? nextIndex : 0;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => {
        if (prev <= 0) return filtered.length - 1;
        return prev - 1;
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
        const opt = filtered[highlightedIndex];
        onChange && onChange(getOptValue(opt));
        setOpen(false);
        setSearch("");
      } else if (filtered.length > 0) {
        const opt = filtered[0];
        onChange && onChange(getOptValue(opt));
        setOpen(false);
        setSearch("");
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setSearch("");
    }
  };

  return (
    <div
      ref={ref}
      className={styles.field}
      style={{ marginBottom: "5px" }}
      onKeyDown={disabled ? undefined : handleKeyDown}
    >
      {label && <label>{label}</label>}
      <div className={styles.dropdown} style={{ display: "flex", alignItems: "center", padding: 0, pointerEvents: disabled ? "none" : "auto", opacity: disabled ? 0.95 : 1 }}>
        <input
          ref={inputRef}
          className={styles.dropdownInput}
          value={open ? search : currentLabel}
          placeholder={placeholder}
          title={currentLabel}
          readOnly={disabled}
          onChange={(e) => {
            if (disabled) return;
            const val = e.target.value;
            setSearch(val);
            setOpen(true);
            if (val === "") {
              onChange && onChange("");
            }
          }}
          onFocus={() => {
            if (disabled) return;
            setSearch("");
            setOpen(true);
          }}
          style={{
            flex: 1,
            border: "none",
            outline: "none",
            background: "transparent",
            padding: "0 8px",
            fontSize: "inherit",
            color: value ? "inherit" : "#999",
            cursor: disabled ? "default" : "text",
            minWidth: 0,
            height: "35px",
            whiteSpace: "nowrap",
          }}
        />
        <span
          className={styles.arrow}
          onMouseDown={(e) => {
            if (disabled) return;
            e.preventDefault(); 
            setOpen((o) => {
              if (!o) setSearch("");
              return !o;
            });
          }}
          style={{
            cursor: disabled ? "default" : "pointer",
            padding: "0 8px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        >
          <DownArrow />
        </span>
      </div>
      {!disabled && open && (
        <div className={styles.dropdownMenu} ref={menuRef}>
          {filtered.length > 0 ? (
            filtered.map((opt, i) => (
              <div
                key={i}
                title={getOptLabel(opt)}
                ref={value === getOptValue(opt) ? activeItemRef : null}
                className={`${styles.dropdownItem} ${value === getOptValue(opt) ? styles.activeItem : ""}`}
                style={{
                  backgroundColor: value === getOptValue(opt) ? "#f4faf5" : highlightedIndex === i ? "#f8f9f9" : undefined,
                  fontWeight: value === getOptValue(opt) ? "500" : undefined,
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  justSelected.current = true;
                  onChange && onChange(getOptValue(opt));
                  setOpen(false);
                  setSearch("");
                }}
              >
                {getOptLabel(opt)}
              </div>
            ))
          ) : (
            <div className={styles.dropdownItem} style={{ color: "#999", pointerEvents: "none" }}>
              No results
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const OperatorBtn = ({ value, onChange, isModifier = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (
        ref.current && 
        !ref.current.contains(e.target) &&
        document.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div ref={ref} style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, width: isModifier ? "100%" : "auto" }}>
      {open && (
        <div className={styles.operatorPopup} style={{ position: "absolute", top: "calc(100% + 4px)", left: "50%", transform: "translateX(-50%)", zIndex: 99 }}>
          {OPERATORS.map((op) => (
            <div
              key={op}
              className={`${styles.operatorItem} ${value === op ? styles.activeOperator : ""}`}
              onClick={() => { onChange(op); setOpen(false); }}
            >
              <OperatorIcon type={op} size={18} />
            </div>
          ))}
        </div>
      )}
      <div
        style={{ display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", width: isModifier ? "100%" : "auto", height: isModifier ? "32px" : "auto", boxSizing: "border-box" }}  
        className={isModifier ? styles.modifierMultiply : styles.addBtn}
        onClick={() => setOpen((o) => !o)}
        title="Change operator"
      >
        <OperatorIcon type={value} size={isModifier ? 16 : 22} />
      </div>
    </div>
  );
};

// ── LinkedCellBlock ───────────────────────────────────────────────────────
const LinkedCellBlock = ({
  elKey,
  openSections,
  setOpenSections,
  modifiers,
  addModifier,
  updateModifierOperator,
  updateModifierValue,
  selections,
  setSelection,
  isFormula,
  moduleOpts = [],
  getSections,
  getSubSections,
  getCols,
  getRows,
  showLabels = false,
  showAddModifier = false,
  removeModifier,
  updateModifierType,
  isPrefilled = false,
  expandedModifiers = {},
  setExpandedModifiers = () => {},
  isReadOnly = false,
}) => {
  const modifierRef = useRef(null);

  const mods = modifiers[elKey] || [];
  const [localShowAllMods, setLocalShowAllMods] = useState(Boolean(expandedModifiers[elKey]));
  const showAllMods = localShowAllMods || Boolean(expandedModifiers[elKey]);
  const setShowAllMods = (val) => {
    setLocalShowAllMods(val);
    setExpandedModifiers((prev) => ({ ...prev, [elKey]: val }));
  };

  useEffect(() => {
    setLocalShowAllMods(Boolean(expandedModifiers[elKey]));
  }, [expandedModifiers, elKey]);

  const handleViewMore = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowAllMods(true);
  };

  useEffect(() => {
    if (!showAllMods) return;
    const handleClickOutside = (event) => {
      if (
        modifierRef.current && 
        !modifierRef.current.contains(event.target) &&
        document.contains(event.target)
      ) {
        setShowAllMods(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showAllMods, elKey, setExpandedModifiers]);

  const toggle = (sec) => {
    setOpenSections((prev) => {
      let isOpen;
      if (sec.endsWith("-module")) {
        isOpen = prev[sec] === undefined ? isPrefilled : prev[sec] === sec;
      } else {
        isOpen = prev[sec] === sec;
      }
      return {
        ...prev,
        [`${elKey}-module`]: "",
        [`${elKey}-cell`]: "",
        [sec]: isOpen ? "" : sec,
      };
    });
  };

  const col = selections[`${elKey}-col`];
  const row = selections[`${elKey}-row`];
  const hasModifiers = mods.length > 0;
  const isModBlock = elKey.startsWith("mod-");

  const isModuleOpen = openSections[`${elKey}-module`] === undefined ? isPrefilled : openSections[`${elKey}-module`] === `${elKey}-module`;

  return (
    <div className={styles.blockContent} ref={modifierRef} style={{ position: "relative" }}>
      <div className={styles.topContainer}>
        <div className={`${styles.headerGroup} ${(!hasModifiers && !isModBlock) ? styles.noInnerBorder : ""}`}>
          <div className={styles.sectionHeader} onClick={() => toggle(`${elKey}-module`)}>
            <span>Select Module</span>
            <DownArrow
              className={styles.arrowIcon}
              style={{
                transform: isModuleOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </div>
          {isModuleOpen && (
            <div className={styles.sectionBody}>
              <Dropdown
                label={showLabels ? "Select Module" : undefined}
                placeholder="Select module"
                options={moduleOpts}
                value={selections[`${elKey}-mod`]}
                onChange={(v) => setSelection(`${elKey}-mod`, v)}
                disabled={isReadOnly}
              />
              <Dropdown
                label={showLabels ? "Select Section" : undefined}
                placeholder="Select section"
                options={getSections(selections[`${elKey}-mod`])}
                value={selections[`${elKey}-sec`]}
                onChange={(v) => setSelection(`${elKey}-sec`, v)}
                disabled={isReadOnly}
              />
              <Dropdown
                label={showLabels ? "Select Sub-Section" : undefined}
                placeholder="Select sub-section"
                options={getSubSections(selections[`${elKey}-mod`], selections[`${elKey}-sec`])}
                value={selections[`${elKey}-sub`]}
                onChange={(v) => setSelection(`${elKey}-sub`, v)}
                disabled={isReadOnly}
              />
            </div>
          )}

          <div className={styles.sectionHeader} onClick={() => toggle(`${elKey}-cell`)}>
            <span>Select Cell</span>
            <DownArrow
              className={styles.arrowIcon}
              style={{
                transform: openSections[`${elKey}-cell`] ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </div>
          {openSections[`${elKey}-cell`] && (
            <div className={styles.sectionBody}>
              {isFormula ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <Dropdown
                    placeholder="Select Column"
                    options={getCols(selections[`${elKey}-mod`], selections[`${elKey}-sec`], selections[`${elKey}-sub`])}
                    value={col}
                    onChange={(v) => setSelection(`${elKey}-col`, v)}
                    disabled={isReadOnly}
                  />
                  <Dropdown
                    placeholder="Select Row"
                    options={getRows(selections[`${elKey}-mod`], selections[`${elKey}-sec`], selections[`${elKey}-sub`])}
                    value={row}
                    onChange={(v) => setSelection(`${elKey}-row`, v)}
                    disabled={isReadOnly}
                  />
                </div>
              ) : (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px" }}>
                    <Dropdown
                      placeholder="Select Column"
                      options={getCols(selections[`${elKey}-mod`], selections[`${elKey}-sec`], selections[`${elKey}-sub`])}
                      value={col}
                      onChange={(v) => setSelection(`${elKey}-col`, v)}
                      disabled={isReadOnly}
                    />
                    <Dropdown
                      placeholder="Select Row"
                      options={getRows(selections[`${elKey}-mod`], selections[`${elKey}-sec`], selections[`${elKey}-sub`])}
                      value={row}
                      onChange={(v) => setSelection(`${elKey}-row`, v)}
                      disabled={isReadOnly}
                    />
                  </div>
                  {col && row && (
                    <div className={styles.selected} style={{ fontSize: "13px", marginTop: "6px" }}>
                      Selected <strong>{row}</strong> in <strong>{col}</strong>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {!showAllMods && hasModifiers && (
        <div className={styles.modifierContainer}>
          <div className={styles.viewMore} onClick={handleViewMore}>
            View {mods.length} more
          </div>
        </div>
      )}



      {showAllMods && hasModifiers && (
        <div
          className="no-scrollbar"
          style={{
            position: "absolute",
            top: "calc(100% - 5px)",
            left: "-9px",
            zIndex: 50,
            width: "calc(100% + 18px)",
            boxSizing: "border-box",
            backgroundColor: "#ffffff",
            padding: "12px",
            border: "1px solid #c2e6d1",
            borderTop: "none",
            borderRadius: "0 0 10px 10px",
            boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
            maxHeight: "350px",
            overflowY: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <style>{`
            .no-scrollbar::-webkit-scrollbar {
              display: none;
            }
          `}</style>

          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "10px" }}>
            {mods.map((mod) => (
              <div key={mod.id} className={styles.modifierCard} style={{ margin: 0, width: "100%", minWidth: "100%", position: "relative" }}>
                <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", marginBottom: "8px" }}>
                  <div className={styles.modifierDelete} onClick={() => removeModifier(elKey, mod.id)} style={{ position: "relative", width: "16px", height: "16px" }}>
                    <DeleteGreyIcon className={styles.deleteDefault} />
                    <DeleteIcon className={styles.deleteHover} />
                  </div>
                </div>

                <div className={styles.modifierTop}>
                  <OperatorBtn
                    value={mod.operator || "＋"}
                    onChange={(op) => updateModifierOperator(elKey, mod.id, op)}
                    isModifier={true}
                  />
                </div>

                {mod.type === "chooser" && (
                  <div className={styles.modifierActions}>
                    <button className={styles.modifierBtn} onClick={() => updateModifierType(elKey, mod.id, "single")}>
                      Single Value
                    </button>
                    <button className={styles.modifierBtn} onClick={() => updateModifierType(elKey, mod.id, "linked")}>
                      Link to Cell
                    </button>
                  </div>
                )}

                {mod.type === "single" && (
                  <div className={styles.modifierValueWrapper}>
                    <input
                      className={styles.modifierInputFull}
                      placeholder="0.00"
                      value={mod.value || ""}
                      onChange={(e) => updateModifierValue(elKey, mod.id, e.target.value)}
                    />
                    <button
                      type="button"
                      className={styles.modifierTypeClose}
                      onClick={() => updateModifierType(elKey, mod.id, "chooser")}
                      title="Change modifier mode"
                    >
                      <RemoveIcon width={16} height={16} />
                    </button>
                  </div>
                )}

                {mod.type === "linked" && (
                  <div className={styles.modifierLinkedWrapper}>
                    <button
                      type="button"
                      className={styles.modifierModeClose}
                      style={{ zIndex: 10, top: "20px", right: "11px", background: "#f4faf5", border: "none", borderRadius: "30%" }}
                      onClick={() => updateModifierType(elKey, mod.id, "chooser")}
                      title="Change modifier mode"
                    >
                      <RemoveIcon width={16} height={16} />
                    </button>
                    <div className={styles.modifierLinked}>
                      <LinkedCellBlock
                        elKey={`mod-${mod.id}`}
                        isFormula={true}
                        openSections={openSections}
                        setOpenSections={setOpenSections}
                        modifiers={{}}
                        selections={selections}
                        setSelection={setSelection}
                        moduleOpts={moduleOpts}
                        getSections={getSections}
                        getSubSections={getSubSections}
                        getCols={getCols}
                        getRows={getRows}
                        showLabels={false}
                        isPrefilled={isPrefilled}
                        expandedModifiers={expandedModifiers}
                        setExpandedModifiers={setExpandedModifiers}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


// ── Formula preview bar ───────────────────────────────────────────────────
const FormulaPreview = ({ baseLabel, terms }) => {
  if (!terms || terms.length === 0) return null;

  const DISPLAY_OP = { "+": "＋", "-": "－", "*": "×", "/": "÷" };

  const parts = terms.map((t, i) => {
    const opDisplay = i === 0 ? "" : ` ${DISPLAY_OP[t.operator] || t.operator} `;
    if (t.type === "constant") return `${opDisplay}${t.value ?? t.constant ?? 0}`;
    if (t.type === "cell") return `${opDisplay}${t.row || t.subSection || t.section || "Cell"}`;
    return opDisplay;
  });

  return (
    <div
      style={{
        background: "#f7f9f8",
        border: "1px solid #CCD5D3",
        borderRadius: "8px",
        padding: "10px 14px",
        marginTop: "14px",
        fontSize: "13px",
        color: "#1a202c",
        lineHeight: "1.6",
      }}
    >
      <div style={{ color: "#999", fontSize: "11px", marginBottom: "4px" }}>Formula</div>
      <div>
        <span style={{ color: "#205A46", fontWeight: 600 }}>
          {baseLabel || "Cell Value"}
        </span>{" "}
        = {parts.join("")}
      </div>
    </div>
  );
};

const SidePanel = ({ showLinkPanel, setShowLinkPanel, onApply, tempId, selectedCell, activeCellKey, disclosureID, type, cashflowWorkingsID, cashflowID, dataID, groupingTemplateID, isTemplate, table, onSaveSuccess, isConsol = false, consolID, isReadOnly = false }) => {
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [linkType,     setLinkType]     = useState("single");
  const [openSections, setOpenSections] = useState({});
  const [selections,   setSelections]   = useState({});
  const [modifiers,    setModifiers]    = useState({});
  const [elements,     setElements]     = useState([]);
  const [operators,    setOperators]    = useState({});
  const [apiOptions,   setApiOptions]   = useState({});
  const finalTemplateID = groupingTemplateID || tempId;
  const MODULE_OPTS = Object.keys(apiOptions);
  const [cellSelections, setCellSelections] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isPanelDataLoading, setIsPanelDataLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [errorData, setErrorData] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [cellModifiers, setCellModifiers] = useState({});
  const [cellElements, setCellElements] = useState({});
  const [cellOperators, setCellOperators] = useState({});
  const [isBold, setIsBold] = useState(false);
  const [isTotal, setIsTotal] = useState(false);
  const [isSubtotal, setIsSubtotal] = useState(false);

  const handleTotalToggle = (val) => {
    setIsTotal(val);
    if (val) {
      setIsBold(true);
      setIsSubtotal(false);
    }
  };

  const handleSubtotalToggle = (val) => {
    setIsSubtotal(val);
    if (val) {
      setIsBold(true);
      setIsTotal(false);
    }
  };
  const panelRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const pendingPanelLoadsRef = useRef(0);
  const matchedFormulaRef = useRef(null);
  const beginPanelDataLoad = () => {
    pendingPanelLoadsRef.current += 1;
    setIsPanelDataLoading(true);
  };
  const endPanelDataLoad = () => {
    pendingPanelLoadsRef.current = Math.max(0, pendingPanelLoadsRef.current - 1);
    setIsPanelDataLoading(pendingPanelLoadsRef.current > 0);
  };
  const COL_MAP = {
    cy: "currentYear",
    py: "previousYear",
    amountCY: "currentYear",
    amountPY: "previousYear",
    currentyear: "currentYear",
    previousyear: "previousYear",
  };
  const formatColName = (col) => {
    if (!col) return "";
    const lower = col.toLowerCase();
    if (lower === "currentyear" || lower === "cy" || lower === "amountcy") {
      return "current year";
    }
    if (lower === "previousyear" || lower === "py" || lower === "amountpy") {
      return "previous year";
    }
    return col;
  };
  const standaloneCategory =
    (type === "cashflow" && cashflowWorkingsID)
      ? "cashflowWorkings"
      : type === "cashflow"
      ? "cashflow"
      : type === "cashflowworkings"
      ? "cashflowworkings"
      : cashflowWorkingsID
      ? "cashflowworkings"
      : type === "socie"
      ? "socie"
      : "disclosure";

  // Saved Cashflow links may use casing or legacy section names that differ
  // from the values currently returned by the formula-options endpoint.
  // Resolve each level against those options before prepopulating the panel.
  const resolveSavedModule = (module) => {
    if (type !== "cashflow") return module || "";
    const normalizedModule = String(module || "").trim().toLowerCase();
    return MODULE_OPTS.find(
      (option) => String(option).trim().toLowerCase() === normalizedModule
    ) || module || "";
  };

  const resolveSavedSection = (module, section, subSection) => {
    if (type === "cashflow" && !isConsol && !isTemplate && module === "cashflow") {
        return subSection || section || "";
    }
    if (type !== "cashflow") return section || "";
    const resolvedModule = resolveSavedModule(module);
    const moduleOptions = apiOptions?.[resolvedModule] || [];
    const normalizedSection = String(section || "").trim().toLowerCase();
    const normalizedSubSection = String(subSection || "").trim().toLowerCase();
    const matchingSection = moduleOptions.find((option, index) =>
      String(option.subgroupID || option.subSections?.[0]?.subgroupID || `${option.section}-${index}`).trim().toLowerCase() === normalizedSection ||
      String(option.section || "").trim().toLowerCase() === normalizedSection
    );
    if (matchingSection) return matchingSection.subgroupID || matchingSection.subSections?.[0]?.subgroupID || `${matchingSection.section}-${moduleOptions.indexOf(matchingSection)}`;
    const matchingSubSection = moduleOptions.find((option) =>
      option.subSections?.some((item) =>
        String(item.subgroupID || "").trim().toLowerCase() === normalizedSubSection ||
        String(item.subSection || "").trim().toLowerCase() === normalizedSubSection
      )
    );
    if (matchingSubSection) return matchingSubSection.subgroupID || matchingSubSection.section;
    return section || "";
  };

  const resolveSavedSubSection = (module, section, subSection) => {
    if (type === "cashflow" && !isConsol && !isTemplate && module === "cashflow") {
        return "";
    }
    if (type !== "cashflow") return subSection || "";
    const resolvedModule = resolveSavedModule(module);
    const resolvedSection = resolveSavedSection(resolvedModule, section, subSection);
    const sectionOption = (apiOptions?.[resolvedModule] || []).find(
      (option) => option.subgroupID === resolvedSection || option.section === resolvedSection
    );
    const normalizedSubSection = String(subSection || "").trim().toLowerCase();
    const matchingItem = sectionOption?.subSections?.find(
      (item) => String(item.subgroupID || "").trim().toLowerCase() === normalizedSubSection || String(item.subSection || "").trim().toLowerCase() === normalizedSubSection
    );
    return matchingItem?.subgroupID || matchingItem?.subSection || subSection || "";
  };
  const [isPrefilled, setIsPrefilled] = useState(false);
  const [expandedModifiers, setExpandedModifiers] = useState({});
  
  const panelWidth = React.useMemo(() => {
    if (linkType !== "formula") return 550;
    const hasBase = selections["base-mod"];
    if (elements.length === 0 && !hasBase) return 550;

    if (isTemplate) {
      return Math.min(980, window.innerWidth * 0.95);
    }
    return Math.min(980, window.innerWidth * 0.95);
  }, [linkType, elements.length, selections, isTemplate]);

  useEffect(() => {
    if (!showLinkPanel) return;

    let optionsRequest = null;

    // CONSOL FLOW
    if (isConsol) {
      const targetConsolID = consolID || groupingTemplateID || tempId || finalTemplateID;
      if (!targetConsolID) return;
      optionsRequest = getConsolFormulaOptionsApiHandler(targetConsolID);
    }
    // TEMPLATE FLOW
    else if (isTemplate) {
      if (!finalTemplateID) return;
      optionsRequest = getFormulaOptions(finalTemplateID);
    }
    // STANDALONE
    else {
      if (!finalTemplateID) return;
      optionsRequest = getPopulateFSFormulaOptions(finalTemplateID);
    }

    beginPanelDataLoad();
    optionsRequest
      .then((res) => {
        if (res?.data?.success) {
          setApiOptions(res.data.options || {});
        }
      })
      .catch(console.error)
      .finally(endPanelDataLoad);
  }, [showLinkPanel, finalTemplateID, consolID, groupingTemplateID, tempId, isConsol, type, isTemplate]);

  // Clear any scheduled close timeout when activeCellKey changes (new cell selected)
  useEffect(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, [activeCellKey]);


// useEffect(() => {
//   if (!activeCellKey) return;
//   setSelections({});
//   setElements([]);
//   setOperators({});
//   setModifiers({});
//   setOpenSections({});
//   setLinkType("single");
//   setIsPrefilled(false);
// }, [activeCellKey]);

useEffect(() => {
  if (!activeCellKey) return;

  // if (cellSelections[activeCellKey]) return;

  setSelections({});
  setElements([]);
  setOperators({});
  setModifiers({});
  setOpenSections({});
  setLinkType("single");
  setIsPrefilled(false);
  setIsBold(false);
  setIsTotal(false);
  setIsSubtotal(false);
}, [activeCellKey]);

useEffect(() => {
  const fetchFormulaData = async () => {
    if (!showLinkPanel || !activeCellKey || !selectedCell) return;

    beginPanelDataLoad();
    try {
      let groupingTemplate = null;
      try {
        const res = isTemplate
          ? await getTemplateDetailsApiHandler({
              groupingTemplateID: finalTemplateID,
            })
          : isConsol
          ? await getConsolPopulateFSDetailsApiHandler({
              consolID: consolID || finalTemplateID,
              category: standaloneCategory,
            })
          : await getPopulateFSDetailsApiHandler({
              projectID: finalTemplateID,
              category: standaloneCategory,
            });

        groupingTemplate = isTemplate
          ? res?.data?.groupingTemplate
          : {
              [standaloneCategory === "disclosure" ? "disclosures" : standaloneCategory]:
                res?.data?.data,
            };
      } catch (err) {
        console.warn("Could not fetch grouping template details, proceeding with table data.", err);
      }
     const normalizedCellKey = `${cashflowWorkingsID || ''}-${dataID || ''}-${selectedCell?.row || ''}-${selectedCell?.col || ''}`;
      const normalize = (v) => String(v || "").trim().toLowerCase();

      // collect formulas from the specific table only
      let freshTableFormulas = [];

     const findInTree = (items = []) => {
  for (const item of items) {
    // Direct match (for grouping/subgroup items)
    if (String(item._id) === String(dataID)) {
      if (item.cellFormulas?.length) {
        freshTableFormulas = item.cellFormulas;
      }
      return true;
    }
    if (Array.isArray(item.data)) {
      for (const block of item.data) {
        if (String(block._id) === String(dataID)) {
          if (block.cellFormulas?.length) {
            freshTableFormulas = block.cellFormulas;
          }
          return true;
        }
      }
    }
    if (item.subgroup && findInTree(item.subgroup)) return true;
    if (item.subNotes && findInTree(item.subNotes)) return true;
    if (item.data && findInTree(item.data)) return true;
  }
  return false;
};

      if (groupingTemplate?.grouping) findInTree(groupingTemplate.grouping);
      if (!freshTableFormulas.length && groupingTemplate?.cashflowWorkings) {
        findInTree(groupingTemplate.cashflowWorkings);
      }
      if (!freshTableFormulas.length && groupingTemplate?.cashflowworkings) {
        findInTree(groupingTemplate.cashflowworkings);
      }
      if (!freshTableFormulas.length && groupingTemplate?.cashflow) {
        findInTree(groupingTemplate.cashflow);
      }
      if (!freshTableFormulas.length && groupingTemplate?.disclosures) {
        findInTree(groupingTemplate.disclosures);
      }
      if (!freshTableFormulas.length && groupingTemplate?.disclosure) {
        findInTree(groupingTemplate.disclosure);
      }
      if (!freshTableFormulas.length && groupingTemplate?.socie) {
        findInTree(groupingTemplate.socie);
      }

      if (!freshTableFormulas.length && table?.cellFormulas?.length) {
        freshTableFormulas = table.cellFormulas;
      }

      const selectedCol =
        COL_MAP[selectedCell.col] || selectedCell.col;
      const selectedRow = normalize(selectedCell.row);
      const selectedRowId = normalize(selectedCell.rowId);
      const cleanRowName = (value) => normalize(value).replace(/[^a-z0-9]/g, "");
      const selectedCleanRow = cleanRowName(selectedCell.row);

      const formula = freshTableFormulas.find((f) => {
        const formulaRow = normalize(f.row || f.rowID || f.rowId);
        const formulaCol = normalize(f.col);
        const formulaCleanRow = cleanRowName(formulaRow);

        const isMatchedByOriginal = Boolean(
          selectedCell?.originalFormulaRow &&
          formulaRow === normalize(selectedCell.originalFormulaRow)
        );

        const isCleanRowMatch = Boolean(
          formulaCleanRow &&
          selectedCleanRow &&
          formulaCleanRow === selectedCleanRow
        );

        const isSmallStandaloneDisclosureRename =
          !isTemplate &&
          type === "disclosure" &&
          selectedCleanRow.length >= 2 &&
          formulaCleanRow.length >= 2 &&
          (selectedCleanRow === formulaCleanRow ||
            (Math.abs(selectedCleanRow.length - formulaCleanRow.length) <= 4 &&
              (selectedCleanRow.startsWith(formulaCleanRow) ||
                formulaCleanRow.startsWith(selectedCleanRow))));

        const isSmallStandaloneWorkingsRename =
          !isTemplate &&
          type === "cashflow" &&
          Boolean(cashflowWorkingsID) &&
          selectedCleanRow.length >= 6 &&
          formulaCleanRow.length >= 6 &&
          Math.abs(selectedCleanRow.length - formulaCleanRow.length) <= 2 &&
          (selectedCleanRow.startsWith(formulaCleanRow) ||
            formulaCleanRow.startsWith(selectedCleanRow));

        const isSmallStandaloneSocieRename =
          !isTemplate &&
          type === "socie" &&
          selectedCleanRow.length >= 4 &&
          formulaCleanRow.length >= 4 &&
          (selectedCleanRow === formulaCleanRow ||
            (Math.abs(selectedCleanRow.length - formulaCleanRow.length) <= 3 &&
              (selectedCleanRow.startsWith(formulaCleanRow) ||
                formulaCleanRow.startsWith(selectedCleanRow))));

        return (
          formulaCol === normalize(selectedCol) &&
          (formulaRow === selectedRow ||
            (selectedRowId && (formulaRow === selectedRowId || normalize(f.rowId || f.rowID) === selectedRowId)) ||
            isMatchedByOriginal ||
            isCleanRowMatch ||
            isSmallStandaloneDisclosureRename ||
            isSmallStandaloneWorkingsRename ||
            isSmallStandaloneSocieRename)
        );
      }) || (
        selectedCell?.initialFormula &&
        (normalize(COL_MAP[selectedCell.initialFormula.col] || selectedCell.initialFormula.col) === normalize(selectedCol))
          ? selectedCell.initialFormula
          : null
      );

      console.log("activeCellKey", activeCellKey);
      console.log("selectedCell", selectedCell);
      console.log("freshTableFormulas", freshTableFormulas);
      console.log("matchedFormula", formula);

      if (!formula) {
        matchedFormulaRef.current = null;
        setSelections({});
        setElements([]);
        setOperators({});
        setModifiers({});
        setLinkType("single");
        setIsPrefilled(false);
        setIsBold(false);
        setIsTotal(false);
        setIsSubtotal(false);
        return;
      }

      matchedFormulaRef.current = formula;

      // SINGLE LINK
      if (formula.type === "link" || (!formula.terms && formula.link)) {
        setLinkType("single");
        setIsPrefilled(true);

        const reverseMap = {
          "+": "＋",
          "-": "－",
          "*": "×",
          "/": "÷",
        };

        const saved = {
          "s-mod": resolveSavedModule(formula.link?.module),
          "s-sec": resolveSavedSection(formula.link?.module, formula.link?.section, formula.link?.subSection),
          "s-sub": resolveSavedSubSection(formula.link?.module, formula.link?.section, formula.link?.subSection),
          "s-row": formula.link?.row || "",
          "s-col": formula.link?.col || "",
        };

        const modsObj = {};
        if (Array.isArray(formula.modifiers) && formula.modifiers.length > 0) {
          modsObj["single"] = formula.modifiers.map((m, mIdx) => {
            const mId = Date.now() + mIdx * 10;
            const isVal = m.type === "value" || m.type === "constant" || m.value !== undefined;
            if (isVal) {
              return {
                id: mId,
                operator: reverseMap[m.operator] || m.operator || "＋",
                type: "single",
                value: String(m.value ?? ""),
              };
            } else {
              saved[`mod-${mId}-mod`] = m.module || "";
              saved[`mod-${mId}-sec`] = m.section || "";
              saved[`mod-${mId}-sub`] = m.subSection || "";
              saved[`mod-${mId}-row`] = m.row || "";
              saved[`mod-${mId}-col`] = m.col || "";
              return {
                id: mId,
                operator: reverseMap[m.operator] || m.operator || "＋",
                type: "linked",
              };
            }
          });
        }

        setSelections(saved);
        setElements([]);
        setOperators({});
        setModifiers(modsObj);
        setIsBold(formula.isBold || false);
        setIsTotal(formula.formulaType === "total");
        setIsSubtotal(formula.formulaType === "subtotal");
      }

      // FORMULA
      if (formula.type === "formula" || formula.terms) {
        setLinkType("formula");
        setIsPrefilled(true);
        setIsBold(formula.isBold || false);
        setIsTotal(formula.formulaType === "total");
        setIsSubtotal(formula.formulaType === "subtotal");

        const terms = formula.terms || [];

        if (terms.length > 0) {
          const first = terms[0];
          const isBaseCell = first.type === "cell" || !!first.module;

          const reverseMap = {
            "+": "＋",
            "-": "－",
            "*": "×",
            "/": "÷",
          };

          const ops = {};
          const els = [];
          const sel = {};
          const modsObj = {};

          if (isBaseCell) {
            sel["base-mod"] = resolveSavedModule(first.module);
            sel["base-sec"] = resolveSavedSection(first.module, first.section, first.subSection);
            sel["base-sub"] = resolveSavedSubSection(first.module, first.section, first.subSection);
            sel["base-row"] = first.row || "";
            sel["base-col"] = first.col || "";
            ops["base"] = reverseMap[first.operator] || "＋";

            if (Array.isArray(first.modifiers) && first.modifiers.length > 0) {
              modsObj["base"] = first.modifiers.map((m, mIdx) => {
                const mId = Date.now() + mIdx * 10;
                const isVal = m.type === "value" || m.type === "constant" || m.value !== undefined;
                if (isVal) {
                  return {
                    id: mId,
                    operator: reverseMap[m.operator] || m.operator || "＋",
                    type: "single",
                    value: String(m.value ?? ""),
                  };
                } else {
                  sel[`mod-${mId}-mod`] = resolveSavedModule(m.module);
                  sel[`mod-${mId}-sec`] = resolveSavedSection(m.module, m.section, m.subSection);
                  sel[`mod-${mId}-sub`] = resolveSavedSubSection(m.module, m.section, m.subSection);
                  sel[`mod-${mId}-row`] = m.row || "";
                  sel[`mod-${mId}-col`] = m.col || "";
                  return {
                    id: mId,
                    operator: reverseMap[m.operator] || m.operator || "＋",
                    type: "linked",
                  };
                }
              });
            }

            terms.slice(1).forEach((t, idx) => {
              const id = Date.now() + (idx + 1) * 100;
              ops[`op-${idx}`] = reverseMap[t.operator] || "＋";

              const isConstant = t.type === "constant" || t.value !== undefined || t.constant !== undefined;
              const isCell = t.type === "cell" || !!t.module;

              if (isConstant) {
                els.push({ id, type: "value", value: String(t.value ?? t.constant ?? "") });
              } else if (isCell) {
                els.push({ id, type: "linked" });
                sel[`el-${id}-mod`] = resolveSavedModule(t.module);
                sel[`el-${id}-sec`] = resolveSavedSection(t.module, t.section, t.subSection);
                sel[`el-${id}-sub`] = resolveSavedSubSection(t.module, t.section, t.subSection);
                sel[`el-${id}-row`] = t.row || "";
                sel[`el-${id}-col`] = t.col || "";

                if (Array.isArray(t.modifiers) && t.modifiers.length > 0) {
                  modsObj[`el-${id}`] = t.modifiers.map((m, mIdx) => {
                    const mId = Date.now() + (idx + 1) * 100 + mIdx + 1;
                    const isVal = m.type === "value" || m.type === "constant" || m.value !== undefined;
                    if (isVal) {
                      return {
                        id: mId,
                        operator: reverseMap[m.operator] || m.operator || "＋",
                        type: "single",
                        value: String(m.value ?? ""),
                      };
                    } else {
                      sel[`mod-${mId}-mod`] = resolveSavedModule(m.module);
                      sel[`mod-${mId}-sec`] = resolveSavedSection(m.module, m.section, m.subSection);
                      sel[`mod-${mId}-sub`] = resolveSavedSubSection(m.module, m.section, m.subSection);
                      sel[`mod-${mId}-row`] = m.row || "";
                      sel[`mod-${mId}-col`] = m.col || "";
                      return {
                        id: mId,
                        operator: reverseMap[m.operator] || m.operator || "＋",
                        type: "linked",
                      };
                    }
                  });
                }
              }
            });
          } else {
            // First term is a constant, move everything to elements
            terms.forEach((t, idx) => {
              const id = Date.now() + (idx + 1) * 100;
              ops[`op-${idx}`] = reverseMap[t.operator] || "＋";

              const isConstant = t.type === "constant" || t.value !== undefined || t.constant !== undefined;
              const isCell = t.type === "cell" || !!t.module;

              if (isConstant) {
                els.push({ id, type: "value", value: String(t.value ?? t.constant ?? "") });
              } else if (isCell) {
                els.push({ id, type: "linked" });
                sel[`el-${id}-mod`] = resolveSavedModule(t.module);
                sel[`el-${id}-sec`] = resolveSavedSection(t.module, t.section, t.subSection);
                sel[`el-${id}-sub`] = resolveSavedSubSection(t.module, t.section, t.subSection);
                sel[`el-${id}-row`] = t.row || "";
                sel[`el-${id}-col`] = t.col || "";

                if (Array.isArray(t.modifiers) && t.modifiers.length > 0) {
                  modsObj[`el-${id}`] = t.modifiers.map((m, mIdx) => {
                    const mId = Date.now() + (idx + 1) * 100 + mIdx + 1;
                    const isVal = m.type === "value" || m.type === "constant" || m.value !== undefined;
                    if (isVal) {
                      return {
                        id: mId,
                        operator: reverseMap[m.operator] || m.operator || "＋",
                        type: "single",
                        value: String(m.value ?? ""),
                      };
                    } else {
                      sel[`mod-${mId}-mod`] = resolveSavedModule(m.module);
                      sel[`mod-${mId}-sec`] = resolveSavedSection(m.module, m.section, m.subSection);
                      sel[`mod-${mId}-sub`] = resolveSavedSubSection(m.module, m.section, m.subSection);
                      sel[`mod-${mId}-row`] = m.row || "";
                      sel[`mod-${mId}-col`] = m.col || "";
                      return {
                        id: mId,
                        operator: reverseMap[m.operator] || m.operator || "＋",
                        type: "linked",
                      };
                    }
                  });
                }
              }
            });
          }

          setOperators(ops);
          setElements(els);
          setSelections((prev) => ({ ...prev, ...sel }));
          setModifiers(modsObj);
          setIsPrefilled(true);
        }
      }
    } catch (err) {
      console.error("Prefill API Error", err);
    } finally {
      endPanelDataLoad();
    }
  };

  fetchFormulaData();
}, [
  activeCellKey,
  selectedCell,
  apiOptions,
  showLinkPanel,
  cashflowWorkingsID,
  type,
  isTemplate,
]);

useEffect(() => {
  if (!activeCellKey) return;

  const savedSel = cellSelections[activeCellKey];
  if (savedSel && Object.keys(savedSel).length > 0) {
    setSelections(savedSel);
    setModifiers(cellModifiers[activeCellKey] || {});
    setElements(cellElements[activeCellKey] || []);
    setOperators(cellOperators[activeCellKey] || {});
  }
}, [activeCellKey]);

const proceedWithDeleteLink = async () => {
  setCellSelections((prev) => {
    const updated = { ...prev };
    delete updated[activeCellKey];
    return updated;
  });

  setCellModifiers((prev) => {
    const updated = { ...prev };
    delete updated[activeCellKey];
    return updated;
  });

  setCellElements((prev) => {
    const updated = { ...prev };
    delete updated[activeCellKey];
    return updated;
  });

  setCellOperators((prev) => {
    const updated = { ...prev };
    delete updated[activeCellKey];
    return updated;
  });

  if (!selectedCell?.row || !selectedCell?.col) return;

  try {
    setIsDeleting(true);

    const finalCol = COL_MAP[selectedCell.col] || selectedCell.col;
    const isWorkings = type === "cashflow" && cashflowWorkingsID;
    const isSocie = type === "socie";
    let deleteApi;

    if (isTemplate) {
      deleteApi = isSocie
        ? deleteSocieCellFormula
        : (
            isWorkings
              ? deleteCashflowCellFormula
              : (
                  type === "cashflow"
                    ? deleteCashflow
                    : deleteDisclosureCellFormula
                )
          );
    } else {
      if (type === "socie") {
        deleteApi = deleteStandaloneSocieCellFormula;
      } else if (type === "cashflow" && cashflowWorkingsID) {
        deleteApi = isConsol 
          ? deleteConsolCashflowWorkingsCellFormula 
          : deleteStandaloneCashflowWorkingsCellFormula;
      } else if (type === "cashflow") {
        deleteApi = isConsol ? deleteConsolCashflowCellFormula : deleteStandaloneCashflowCellFormula;
      } else {
        deleteApi = isConsol ? deleteConsolDisclosureCellFormula : deleteStandaloneDisclosureCellFormula;
      }
    }
      
    let params;

    if (isTemplate) {
      const effectiveDataID = (isValidObjectId(dataID) ? dataID : table?._id) || dataID;
      params = isSocie
        ? {
            groupingTemplateID: finalTemplateID,
            socieID: disclosureID,
            dataID: effectiveDataID,
            row: selectedCell.row,
            col: finalCol,
          }
        : (
            isWorkings
              ? {
                  groupingTemplateID: finalTemplateID,
                  cashflowWorkingsID: cashflowWorkingsID || null,
                  dataID: effectiveDataID,
                  row: selectedCell.row,
                  col: finalCol,
                }
              : (
                  type === "cashflow"
                    ? {
                        groupingTemplateID: finalTemplateID,
                        cashflowID: cashflowID || disclosureID,
                        dataID: effectiveDataID,
                        row: selectedCell.row,
                        col: finalCol,
                      }
                    : {
                        groupingTemplateID: finalTemplateID,
                        disclosureID,
                        dataID: effectiveDataID,
                        row: selectedCell.row,
                        col: finalCol,
                      }
                )
          );
    } else {
      if (type === "socie") {
        params = {
          projectID: finalTemplateID,
          row: selectedCell.row,
          col: finalCol,
        };
      } else if (type === "cashflow" && cashflowWorkingsID) {
        params = {
          projectID: finalTemplateID,
          cashflowWorkingsID,
          subgroupID: dataID,
          row: selectedCell.row,
          col: finalCol,
        };
      } else if (type === "cashflow") {
        params = {
          projectID: finalTemplateID,
          ...(isConsol && { consolID }),
          row: selectedCell.row,
          col: finalCol,
        };
      } else {
        params = {
          ...(isConsol ? { consolID: consolID || finalTemplateID } : { projectID: finalTemplateID }),
          disclosureID,
          subgroupID: dataID,
          row: selectedCell.row,
          col: finalCol,
        };
      }
    }

    await deleteApi(params);

    if (
      matchedFormulaRef.current?.row &&
      String(matchedFormulaRef.current.row).trim().toLowerCase() !==
        String(selectedCell.row).trim().toLowerCase()
    ) {
      try {
        await deleteApi({ ...params, row: matchedFormulaRef.current.row });
      } catch (e) {
        console.warn("Could not delete old formula by original row name", e);
      }
    }

    matchedFormulaRef.current = null;
    setSelections({});
    setElements([]);
    setOperators({});
    setModifiers({});
    setOpenSections({});
    setLinkType("single");
    setIsPrefilled(false);

    onApply && onApply(null);
    setShowLinkPanel(false);             
    
    setTimeout(() => {
      onSaveSuccess && onSaveSuccess();
    }, 0);

    setSuccessMessage("Deleted Successfully!");
    setShowSuccessPopup(true);
  } catch (err) {
    console.error("DELETE ERROR", err?.response || err);
    const backendError =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      "Delete failed";

    setErrorData({
      title: "Error",
      message: backendError,
    });
  } finally {
    setIsDeleting(false);
  }
};

const handleDeleteLink = async () => {
  setDeleteConfirm({
    title: "Are you sure?",
    message: "This link will be deleted permanently",
    onConfirm: async () => {
      setDeleteConfirm(null);
      await proceedWithDeleteLink();
    }
  });
};


  const getSections = (module) => {
    if (!module || !apiOptions[module]) return [];
    return apiOptions[module].map((item, idx) => {
      if (type === "cashflow" && !isConsol && !isTemplate && module === "cashflow") {
        return { label: item.section, value: item.section };
      }
      return { label: item.section, value: item.subgroupID || item.subSections?.[0]?.subgroupID || `${item.section}-${idx}` };
    });
  };

  const getSubSections = (module, section) => {
    if (!module || !section || !apiOptions[module]) return [];
    // section is now the subgroupID (or section name if subgroupID is missing)
    const found = apiOptions[module].find((item, idx) => item.subgroupID === section || item.subSections?.[0]?.subgroupID === section || `${item.section}-${idx}` === section || item.section === section);
    return found && found.subSections ? found.subSections.map((s, idx) => ({ label: s.subSection, value: s.subgroupID || `${s.subSection}-${idx}` })) : [];
  };

  const getSubSectionMeta = (module, section, subSection) => {
    if (!module || !section || !subSection || !apiOptions[module]) return null;
    const sec = apiOptions[module].find((item, idx) => item.subgroupID === section || item.subSections?.[0]?.subgroupID === section || `${item.section}-${idx}` === section || item.section === section);
    if (!sec || !sec.subSections) return null;
    return sec.subSections.find((s, idx) => s.subgroupID === subSection || `${s.subSection}-${idx}` === subSection || s.subSection === subSection) || null;
  };

  const getCols = (module, section, subSection) => {
    const meta = getSubSectionMeta(module, section, subSection);
    return meta?.cols || [];
  };

  const getRows = (module, section, subSection) => {
    const meta = getSubSectionMeta(module, section, subSection);
    return meta?.rows || [];
  };

  if (!showLinkPanel) {
    return (
      <>
        <DeletePopup
          isOpen={deleteConfirm !== null}
          title={deleteConfirm?.title}
          message={deleteConfirm?.message}
          onConfirm={deleteConfirm?.onConfirm}
          onCancel={() => setDeleteConfirm(null)}
        />
        <ErrorPopup
          isOpen={errorData !== null}
          title={errorData?.title}
          message={errorData?.message}
          onClose={() => setErrorData(null)}
        />
        <SuccessPopup
          isOpen={showSuccessPopup}
          message={successMessage}
          onClose={() => setShowSuccessPopup(false)}
        />
      </>
    );
  }

  const setSelection = (k, v) => {
  setSelections((prev) => {
    const updated = { ...prev, [k]: v };

    setCellSelections((all) => ({
      ...all,
      [activeCellKey]: updated,
    }));

    return updated;
  });
};

  const addModifier = (k) => {
    setModifiers((prev) => {
      const updated = {
        ...prev,
       [k]: [
        ...(prev[k] || []),
        {
          id: Date.now(),
          operator: "＋",
          type: "chooser",
          value: "",
        },
      ],
      };

      setCellModifiers((all) => ({
        ...all,
        [activeCellKey]: updated,
      }));

      return updated;
    });
  };
 const updateModifierValue = (k, id, v) => {
  setModifiers((prev) => {
    const updated = {
      ...prev,
      [k]: prev[k].map((m) => (m.id === id ? { ...m, value: v } : m)),
    };

    setCellModifiers((all) => ({
      ...all,
      [activeCellKey]: updated,
    }));

    return updated;
  });
};

const updateModifierType = (k, id, type) => {
  setModifiers((prev) => {
    const updated = {
      ...prev,
      [k]: prev[k].map((m) =>
        m.id === id ? { ...m, type } : m
      ),
    };

    setCellModifiers((all) => ({
      ...all,
      [activeCellKey]: updated,
    }));

    return updated;
  });

  if (type === "linked") {
    setOpenSections((prev) => ({
      ...prev,
      [`mod-${id}-module`]: `mod-${id}-module`,
    }));
  }
};


  const removeModifier = (k, id) =>
    setModifiers((p) => ({ ...p, [k]: p[k].filter((m) => m.id !== id) }));
 
  const addElement = () => {
  setElements((prev) => {
    const updated = [...prev, { id: Date.now(), type: "chooser", value: "" }];

    setCellElements((all) => ({
      ...all,
      [activeCellKey]: updated,
    }));

    return updated;
  });
};

const removeElement = (id) => {
  setElements((prev) => {
    const updated = prev.filter((el) => el.id !== id);

    setCellElements((all) => ({
      ...all,
      [activeCellKey]: updated,
    }));

    return updated;
  });

  setSelections((prev) => {
    const updated = { ...prev };
    Object.keys(updated).forEach((key) => {
      if (key.startsWith(`el-${id}`)) {
        delete updated[key];
      }
    });

    setCellSelections((all) => ({
      ...all,
      [activeCellKey]: updated,
    }));

    return updated;
  });
};

const updateModifierOperator = (k, id, op) => {
  setModifiers((prev) => {
    const updated = {
      ...prev,
      [k]: prev[k].map((m) =>
        m.id === id ? { ...m, operator: op } : m
      ),
    };

    setCellModifiers((all) => ({
      ...all,
      [activeCellKey]: updated,
    }));

    return updated;
  });
};

const setElType = (id, t) => {
  setElements((prev) => {
    const updated = prev.map((el) =>
      el.id === id ? { ...el, type: t, value: "" } : el
    );

    setCellElements((all) => ({
      ...all,
      [activeCellKey]: updated,
    }));

    return updated;
  });

  if (t === "linked") {
    setOpenSections((prev) => ({
      ...prev,
      [`el-${id}-module`]: `el-${id}-module`,
    }));
  }
};

const setElValue = (id, v) => {
  setElements((prev) => {
    const updated = prev.map((el) =>
      el.id === id ? { ...el, value: v } : el
    );

    setCellElements((all) => ({
      ...all,
      [activeCellKey]: updated,
    }));

    return updated;
  });
};

  const setOperator = (k, v) => {
  setOperators((prev) => {
    const updated = { ...prev, [k]: v };

    setCellOperators((all) => ({
      ...all,
      [activeCellKey]: updated,
    }));

    return updated;
  });
};
  const buildModifiersPayload = (key) => {
    const rawMods = modifiers[key] || [];
    if (!rawMods.length) return undefined;

    const mapped = rawMods
      .map((m) => {
        const op = OPERATOR_MAP[m.operator || "＋"] || m.operator || "+";
        if (m.type === "single" || m.type === "value") {
          const valNum = m.value !== "" && !isNaN(Number(m.value)) ? Number(m.value) : 0;
          return {
            operator: op,
            type: "value",
            value: valNum,
          };
        }
        if (m.type === "linked" || m.type === "cell" || m.type === "ref") {
          const modKey = `mod-${m.id}`;
          const mod = selections[`${modKey}-mod`];
          const sec = selections[`${modKey}-sec`];
          const sub = selections[`${modKey}-sub`];
          const row = selections[`${modKey}-row`];
          const col = selections[`${modKey}-col`];

          const refMod = {
            operator: op,
            type: isTemplate ? "cell" : "ref",
          };
          if (mod) refMod.module = mod;
          if (sec) refMod.section = sec;
          if (sub) refMod.subSection = sub;
          if (row !== undefined && row !== null) refMod.row = String(row);
          if (col) refMod.col = COL_MAP[col] || col;
          return refMod;
        }
        return null;
      })
      .filter(Boolean);

    return mapped.length > 0 ? mapped : undefined;
  };

  const buildFormulaTerms = () => {
    const terms = [];

    const baseMod = selections["base-mod"];
    const baseSec = selections["base-sec"];
    const baseSub = selections["base-sub"];
    const baseRow = selections["base-row"];
    const baseCol = selections["base-col"];

    if (baseMod && baseRow && baseCol) {
      const baseTerm = {
        type: isTemplate ? "cell" : "ref",
      };

      if (baseMod) baseTerm.module = baseMod;
      if (baseSec) baseTerm.section = baseSec;
      if (baseSub) baseTerm.subSection = baseSub;
      if (baseRow !== undefined && baseRow !== null) baseTerm.row = String(baseRow);
      if (baseCol) baseTerm.col = COL_MAP[baseCol] || baseCol;

      terms.push(baseTerm);

      const baseRawMods = modifiers["base"] || [];
      baseRawMods.forEach((m) => {
        const op = OPERATOR_MAP[m.operator || "＋"] || m.operator || "+";
        if (m.type === "single" || m.type === "value") {
          const valNum = m.value !== "" && !isNaN(Number(m.value)) ? Number(m.value) : 0;
          terms.push({
            operator: op,
            type: isTemplate ? "constant" : "value",
            [isTemplate ? "constant" : "value"]: valNum,
          });
        } else if (m.type === "linked" || m.type === "cell" || m.type === "ref") {
          const modKey = `mod-${m.id}`;
          const mod = selections[`${modKey}-mod`];
          const sec = selections[`${modKey}-sec`];
          const sub = selections[`${modKey}-sub`];
          const row = selections[`${modKey}-row`];
          const col = selections[`${modKey}-col`];

          const refMod = {
            operator: op,
            type: isTemplate ? "cell" : "ref",
          };
          if (mod) refMod.module = mod;
          if (sec) refMod.section = sec;
          if (sub) refMod.subSection = sub;
          if (row !== undefined && row !== null) refMod.row = String(row);
          if (col) refMod.col = COL_MAP[col] || col;
          terms.push(refMod);
        }
      });
    }

    elements.forEach((el, idx) => {
      const op = OPERATOR_MAP[operators[`op-${idx}`] || "＋"];
      const hasBase = !!(selections["base-mod"] && selections["base-row"] && selections["base-col"]);
      const isFirstTerm = !hasBase && idx === 0;

      if (el.type === "value") {
        terms.push({
          ...(isFirstTerm ? {} : { operator: op }),
          type: isTemplate ? "constant" : "value",
          [isTemplate ? "constant" : "value"]:
            el.value !== "" && !isNaN(Number(el.value))
              ? Number(el.value)
              : null,
        });
      }

      if (el.type === "linked") {
        const k = `el-${el.id}`;
        const elTerm = {
          ...(isFirstTerm ? {} : { operator: op }),
          type: isTemplate ? "cell" : "ref",
        };
        const mod = selections[`${k}-mod`];
        const sec = selections[`${k}-sec`];
        const sub = selections[`${k}-sub`];
        const row = selections[`${k}-row`];
        const col = selections[`${k}-col`];

        if (mod) elTerm.module = mod;
        if (sec) elTerm.section = sec;
        if (sub) elTerm.subSection = sub;
        if (row !== undefined && row !== null) elTerm.row = String(row);
        if (col) elTerm.col = COL_MAP[col] || col;

        terms.push(elTerm);

        const elRawMods = modifiers[k] || [];
        elRawMods.forEach((m) => {
          const modOp = OPERATOR_MAP[m.operator || "＋"] || m.operator || "+";
          if (m.type === "single" || m.type === "value") {
            const valNum = m.value !== "" && !isNaN(Number(m.value)) ? Number(m.value) : 0;
            terms.push({
              operator: modOp,
              type: isTemplate ? "constant" : "value",
              [isTemplate ? "constant" : "value"]: valNum,
            });
          } else if (m.type === "linked" || m.type === "cell" || m.type === "ref") {
            const modKey = `mod-${m.id}`;
            const mMod = selections[`${modKey}-mod`];
            const mSec = selections[`${modKey}-sec`];
            const mSub = selections[`${modKey}-sub`];
            const mRow = selections[`${modKey}-row`];
            const mCol = selections[`${modKey}-col`];

            const refMod = {
              operator: modOp,
              type: isTemplate ? "cell" : "ref",
            };
            if (mMod) refMod.module = mMod;
            if (mSec) refMod.section = mSec;
            if (mSub) refMod.subSection = mSub;
            if (mRow !== undefined && mRow !== null) refMod.row = String(mRow);
            if (mCol) refMod.col = COL_MAP[mCol] || mCol;
            terms.push(refMod);
          }
        });
      }
    });

    return terms;
  };

  const handleClose = () => setShowLinkPanel(false);



  const handleApply = async () => {
    /*
    const actionName = isPrefilled ? "Update" : "Apply";
    const confirm = await Swal({
      title: "Are you sure?",
      text: `Do you want to ${actionName.toLowerCase()} this link?`,
      className: "custom-confirm-swal",
      buttons: {
        cancel: {
          text: "No",
          value: null,
          visible: true,
          className: "swal-custom-cancel-btn",
          closeModal: true,
        },
        confirm: {
          text: "Yes",
          value: true,
          visible: true,
          closeModal: true
        }
      },
    });

    if (confirm) {
      await proceedWithApply();
    }
    */
    await proceedWithApply();
  };

  const proceedWithApply = async () => {
    
    console.log("COMPARE");
    console.log("dataID", dataID);
    console.log("table._id", table?._id);
    console.log("cashflowID", cashflowID);
    if (!selectedCell?.row || !selectedCell?.col) {
        console.error("Invalid selectedCell", selectedCell);
        return;
      }
    const effectiveDataID = (isValidObjectId(dataID) ? dataID : table?._id) || dataID;

    if (isTemplate && (type === "socie" || type === "cashflow" || type === "disclosure")) {
      if (!effectiveDataID || !isValidObjectId(effectiveDataID)) {
        Swal(
          "Action Required",
          "Please save the template first to assign a permanent ID to this table before linking cells.",
          "info"
        );
        return;
      }
    }

    if (isTemplate && type === "cashflow") {
      if (
        cashflowWorkingsID &&
        !isValidObjectId(cashflowWorkingsID)
      ) {
        console.warn(
          "Invalid cashflowWorkingsID format",
          cashflowWorkingsID
        );
      }

      if (
        cashflowID &&
        !isValidObjectId(cashflowID)
      ) {
        console.warn(
          "Invalid cashflowID format",
          cashflowID
        );
      }
    }

    console.log("Elements ", elements);
    console.log("Operators", operators);
    console.log("Param Check ", { tempId, disclosureID, dataID, selectedCell });

    let body = null;

  const finalCol = COL_MAP[selectedCell.col] || selectedCell.col;

  if (linkType === "single") {
    const singleMods = (modifiers["single"] || modifiers["base"] || []).filter(Boolean);
    if (singleMods.length > 0) {
      const linkTerm = {
        type: isTemplate ? "cell" : "ref",
        module: selections["s-mod"] || "",
        section: selections["s-sec"] || "",
        subSection: selections["s-sub"] || "",
        row: selections["s-row"] !== undefined && selections["s-row"] !== null ? String(selections["s-row"]) : "",
        col: COL_MAP[selections["s-col"]] || selections["s-col"] || "",
      };
      const modTerms = singleMods.map((m) => {
        const op = OPERATOR_MAP[m.operator || "＋"] || m.operator || "+";
        if (m.type === "single" || m.type === "value") {
          return {
            operator: op,
            type: isTemplate ? "constant" : "value",
            [isTemplate ? "constant" : "value"]: m.value !== "" && !isNaN(Number(m.value)) ? Number(m.value) : 0,
          };
        } else {
          return {
            operator: op,
            type: isTemplate ? "cell" : "ref",
            module: selections[`mod-${m.id}-mod`] || "",
            section: selections[`mod-${m.id}-sec`] || "",
            subSection: selections[`mod-${m.id}-sub`] || "",
            row: selections[`mod-${m.id}-row`] !== undefined && selections[`mod-${m.id}-row`] !== null ? String(selections[`mod-${m.id}-row`]) : "",
            col: COL_MAP[selections[`mod-${m.id}-col`]] || selections[`mod-${m.id}-col`] || "",
          };
        }
      });
      body = {
        row: selectedCell.row !== undefined && selectedCell.row !== null ? String(selectedCell.row) : "",
        col: finalCol,
        type: "formula",
        terms: [linkTerm, ...modTerms],
        isBold: isBold || isTotal || isSubtotal,
        formulaType: isTotal ? "total" : isSubtotal ? "subtotal" : null,
      };
    } else {
      const link = {
        module: selections["s-mod"] || "",
        section: selections["s-sec"] || "",
        subSection: selections["s-sub"] || "",
        row: selections["s-row"] !== undefined && selections["s-row"] !== null ? String(selections["s-row"]) : "",
        col:
          COL_MAP[selections["s-col"]] ||
          selections["s-col"] ||
          "",
      };

      body = {
        row: selectedCell.row !== undefined && selectedCell.row !== null ? String(selectedCell.row) : "",
        col: finalCol,
        type: "link",
        link,
        isBold: isBold || isTotal || isSubtotal,
        formulaType: isTotal ? "total" : isSubtotal ? "subtotal" : null,
      };
    }
  } else {
    const terms = buildFormulaTerms();

    if (terms.length === 0) {
      console.warn("No valid terms, saving empty formula");
    }

    body = {
      row: selectedCell.row !== undefined && selectedCell.row !== null ? String(selectedCell.row) : "",
      col: finalCol,   
      type: "formula",
      terms,
      isBold: isBold || isTotal || isSubtotal,
      formulaType: isTotal ? "total" : isSubtotal ? "subtotal" : null,
    };
  }

    console.log("PUT REQUEST");
    console.log("PARAMS ", { groupingTemplateID: finalTemplateID, disclosureID, dataID });
    console.log("PUT Body", body);
    console.log("[SidePanel Apply Debug]", {
      type,
      isTemplate,
      selectedCell,
      activeCellKey,
      cashflowWorkingsID,
      cashflowID,
      disclosureID,
      dataID,
      tableIdFromProp: table?._id,
      tableTitleFromProp: table?.tableTitle,
      availableTableRows: table?.tableRow?.map((row) => row?.name),
      availableCellFormulaRows: table?.cellFormulas?.map((formula) => ({
        row: formula?.row,
        col: formula?.col,
        type: formula?.type,
      })),
    });

    try {
      setIsSaving(true);

      const isWorkings = type === "cashflow" && cashflowWorkingsID;
      const isSocie = type === "socie";
      let saveApi;

if (isTemplate) {

  // EXISTING TEMPLATE FLOW
  saveApi = isSocie
    ? setSocieCellFormula
    : (
        isWorkings
          ? setCashflowCellFormula
          : (
              type === "cashflow"
                ? setCashflow
                : setDisclosureCellFormula
            )
      );

} else {

  // STANDALONE FLOW
  if (type === "socie") {

    saveApi = setStandaloneSocieCellFormula;

  } else if (
    type === "cashflow" &&
    cashflowWorkingsID
  ) {

    saveApi = isConsol
      ? setConsolCashflowWorkingsCellFormula
      : setStandaloneCashflowWorkingsCellFormula;

  } else if (type === "cashflow") {

    saveApi = isConsol ? setConsolCashflowCellFormula : setStandaloneCashflowCellFormula;

  } else {
    saveApi = isConsol
      ? setConsolDisclosureCellFormula
      : setStandaloneDisclosureCellFormula;

  }

}

let params;

if (isTemplate) {

  // EXISTING TEMPLATE PARAMS
  params = isSocie
    ? {
        groupingTemplateID: finalTemplateID,
        socieID: disclosureID,
        dataID: effectiveDataID,
      }
    : (
        isWorkings
          ? {
              groupingTemplateID: finalTemplateID,
              cashflowWorkingsID,
              dataID: effectiveDataID,
            }
          : (
              type === "cashflow"
                ? {
                    groupingTemplateID: finalTemplateID,
                    cashflowID: cashflowID || disclosureID,
                    dataID: effectiveDataID,
                  }
                : {
                    groupingTemplateID: finalTemplateID,
                    disclosureID,
                    dataID: effectiveDataID,
                  }
            )
      );

} else {

  // STANDALONE PARAMS
  if (type === "socie") {

    params = {
      projectID: finalTemplateID,
    };

  } else if (
    type === "cashflow" &&
    cashflowWorkingsID
  ) {

    params = {
      projectID: finalTemplateID,
      cashflowWorkingsID,
      subgroupID: dataID,
    };

  } else if (type === "cashflow") {

    params = {
      projectID: finalTemplateID,
      ...(isConsol && { consolID }),
    };

  } else {

    params = {
      ...(isConsol ? { consolID: consolID || finalTemplateID } : { projectID: finalTemplateID }),
      disclosureID,
      subgroupID: dataID,
    };
    console.log("DISCLOSURE PARAMS", params);
    

  }

}
      if (
        matchedFormulaRef.current?.row &&
        String(matchedFormulaRef.current.row).trim().toLowerCase() !==
          String(selectedCell.row).trim().toLowerCase()
      ) {
        try {
          const deleteOldParams = isTemplate
            ? {
                groupingTemplateID: finalTemplateID,
                disclosureID,
                dataID: effectiveDataID,
                row: matchedFormulaRef.current.row,
                col: finalCol,
              }
            : {
                ...(isConsol ? { consolID: consolID || finalTemplateID } : { projectID: finalTemplateID }),
                disclosureID,
                subgroupID: dataID,
                row: matchedFormulaRef.current.row,
                col: finalCol,
              };
          const delFn = isTemplate
            ? deleteDisclosureCellFormula
            : (isConsol ? deleteConsolDisclosureCellFormula : deleteStandaloneDisclosureCellFormula);
          await delFn(deleteOldParams);
        } catch (cleanupErr) {
          console.warn("Could not clean up old formula under previous row name", cleanupErr);
        }
      }

      const res = await saveApi(params, body);
      if (res?.data?.success) {
        matchedFormulaRef.current = body;
        if (linkType === "single") {
          const saved = {
            "s-mod": selections["s-mod"],
            "s-sec": selections["s-sec"],
            "s-sub": selections["s-sub"],
            "s-row": selections["s-row"],
            "s-col": selections["s-col"],
          };

          setSelections((prev) => ({
            ...prev,
            ...saved,
          }));

          setCellSelections((all) => ({
            ...all,
            [activeCellKey]: saved,
          }));
        }
        const freshData = res.data?.updatedGroupingTemplate || res.data?.updatedProject || res.data?.data;
        onApply && onApply(body, freshData);
        onSaveSuccess && onSaveSuccess(freshData);
        setShowLinkPanel(false);
        setSuccessMessage(isPrefilled ? "Updated Successfully!" : "Applied Successfully!");
        setShowSuccessPopup(true);
      } else {
        setErrorData({
          title: "Error",
          message: res?.data?.message || "Something went wrong. Please try again.",
        });
      }
      } catch (err) {

  console.error(
    "PUT API ERROR",
    err?.response || err
  );

  const backendError =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.errors?.[0]?.message ||
    "Something went wrong. Please try again.";

  setErrorData({
    title: "Error",
    message: backendError,
  });

}finally {
      setIsSaving(false);
    }
  };

  const compileFormulaPreview = () => {
    const parts = [];
    const baseRow = selections["base-row"];
    const baseMod = selections["base-mod"];
    const baseCol = selections["base-col"];
    const hasBase = !!(baseMod && baseRow && baseCol);

    const renderMods = (elKey) => {
      const mods = modifiers[elKey] || [];
      return mods.map((mod) => {
        const rawOp = mod.operator || "＋";
        const displayOp = OPERATOR_MAP[rawOp] || rawOp;
        const modRenderOp = (
          <span style={{ display: "inline-flex", alignItems: "center", gap: "2px", verticalAlign: "middle" }}>
            <span style={{ color: "#99ABA7" }}>(</span>
            <span style={{ display: "flex", alignItems: "center", transform: "translateY(-1.5px)", fontWeight: "500" }}><OperatorIcon type={displayOp} size={16} strokeWidth={3} /></span>
            <span style={{ color: "#99ABA7" }}>)</span>
          </span>
        );
        let modContent = "(?)";
        if (mod.type === "single") {
          modContent = mod.value || "0";
        } else if (mod.type === "linked") {
          const modRow = selections[`mod-${mod.id}-row`] || "(?)";
          const modSub = selections[`mod-${mod.id}-sub`] || "";
          modContent = modRow?.toLowerCase() === "total" ? `${modSub} (Total)` : modRow;
        }
        return <React.Fragment key={`${elKey}-mod-${mod.id}`}> {modRenderOp} {modContent}</React.Fragment>;
      });
    };

    const renderWithMods = (label, elKey) => {
      const mods = modifiers[elKey] || [];
      if (!mods.length) return label;
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
          <span style={{ color: "#99ABA7" }}>(</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <span>{label}</span>
            {renderMods(elKey)}
          </span>
          <span style={{ color: "#99ABA7" }}>)</span>
        </span>
      );
    };

    if (hasBase) {
      const baseDisplay =
        baseRow?.toLowerCase() === "total"
          ? `${selections["base-sub"]} (Total)`
          : baseRow;

      parts.push(<React.Fragment key="base">{renderWithMods(baseDisplay, "base")}</React.Fragment>);
    }

    elements.forEach((el, idx) => {
      const rawOp = operators[`op-${idx}`] || "＋";
      const displayOp = OPERATOR_MAP[rawOp] || rawOp;
      const renderOp = (hasBase || idx > 0) ? (
        <span style={{ display: "inline-flex", alignItems: "center", gap: "2px", verticalAlign: "middle" }}>
          <span style={{ color: "#99ABA7" }}>(</span>
          <span style={{ display: "flex", alignItems: "center", transform: "translateY(-1.5px)", fontWeight: "500" }}><OperatorIcon type={displayOp} size={16} strokeWidth={3} /></span>
          <span style={{ color: "#99ABA7" }}>)</span>
        </span>
      ) : "";

      if (el.type === "value") {
        parts.push(<React.Fragment key={`val-${idx}`}> {renderOp} {el.value || "0"}</React.Fragment>);
      } else if (el.type === "linked") {
        const linkedRow = selections[`el-${el.id}-row`] || "(?)";
        const linkedSub =
          selections[`el-${el.id}-sub`] || "";

        const linkedDisplay =
          linkedRow?.toLowerCase() === "total"
            ? `${linkedSub} (Total)`
            : linkedRow;

        parts.push(<React.Fragment key={`link-${idx}`}> {renderOp} {renderWithMods(linkedDisplay, `el-${el.id}`)}</React.Fragment>);
      } else {
        parts.push(<React.Fragment key={`q-${idx}`}> {renderOp} (?)</React.Fragment>);
      }
    });

    if (parts.length === 0) {
      return "(?)";
    }
    return parts;
  };

  // renderSingle: labels shown, col+row side by side, selected text shown ──
  const renderSingle = () => {
    const mod = selections["s-mod"];
    const sec = selections["s-sec"];
    const sub = selections["s-sub"];

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        <Dropdown
          label="Select Module"
          placeholder="Select module"
          options={MODULE_OPTS}
          value={mod}
          onChange={(v) => setSelection("s-mod", v)}
          disabled={isReadOnly}
        />
        <Dropdown
          label="Select Section"
          placeholder="Select section"
          options={getSections(mod)}
          value={sec}
          onChange={(v) => setSelection("s-sec", v)}
          disabled={isReadOnly}
        />
        <Dropdown
          label="Select Sub-Section"
          placeholder="Select sub-section"
          options={getSubSections(mod, sec)}
          value={sub}
          onChange={(v) => setSelection("s-sub", v)}
          disabled={isReadOnly}
        />
        <div className={styles.field} style={{ marginBottom: 0 }}>
          <label>Select Cell</label>
          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "14px" }}>
            <Dropdown
              placeholder="Select Column"
              options={getCols(mod, sec, sub)}
              value={selections["s-col"]}
              onChange={(v) => setSelection("s-col", v)}
              disabled={isReadOnly}
            />
            <Dropdown
              placeholder="Select Row"
              options={getRows(mod, sec, sub)}
              value={selections["s-row"]}
              onChange={(v) => setSelection("s-row", v)}
              disabled={isReadOnly}
            />
          </div>
        </div>
        {selections["s-col"] && selections["s-row"] && (
         <div
            className={styles.selected}
            style={{ whiteSpace: "normal", overflow: "visible", textOverflow: "unset" }}
          >
          Selected <strong>{selections["s-row"]}</strong> in{" "}
          <strong>{selections["s-col"]}</strong>
        </div>
        )}
      </div>
    );
  };

  // ── renderFormula: no labels, col+row on separate lines, no selected text ──
  const renderFormula = () => {
    // 1. Collect all elements to be rendered
    const items = [];
    
    if (selections["base-mod"]) {
      // Base Block
      items.push({
        id: "base",
        content: (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div className={styles.leftWrapper}>
              <div className={`${styles.formulaLeft} ${isPrefilled ? styles.prefilledContainer : ""} ${modifiers["base"]?.length > 0 ? styles.whiteBlock : ""}`} style={{ 
                position: "relative", 
                width: "235px", 
                maxWidth: "235px",
                borderBottomLeftRadius: expandedModifiers["base"] ? 0 : 10,
                borderBottomRightRadius: expandedModifiers["base"] ? 0 : 10,
                borderBottomColor: expandedModifiers["base"] ? "transparent" : "#c2e6d1"
              }}>
                {!isReadOnly && isPrefilled && (
                  <div
                    className={styles.baseDelete}
                    onClick={handleDeleteLink}
                    title="Delete link"
                  >
                    <RemoveIcon width={14} height={14} />
                  </div>
                )}
                <LinkedCellBlock
                  key={`base-${activeCellKey}`}
                  elKey="base"
                  isFormula={true}
                  openSections={openSections}
                  setOpenSections={setOpenSections}
                  modifiers={modifiers}
                  addModifier={addModifier}
                  updateModifierOperator={updateModifierOperator}
                  updateModifierValue={updateModifierValue}
                  selections={selections}
                  setSelection={setSelection}
                  moduleOpts={MODULE_OPTS}
                  getSections={getSections}
                  getSubSections={getSubSections}
                  getCols={getCols}
                  getRows={getRows}
                  showLabels={false}
                  showAddModifier={false}
                  removeModifier={removeModifier}
                  updateModifierType={updateModifierType}
                  isPrefilled={isPrefilled}
                  expandedModifiers={expandedModifiers}
                  setExpandedModifiers={setExpandedModifiers}
                  isReadOnly={isReadOnly}
                />
              </div>
              {!isReadOnly && (
                <div
                  className={styles.addModifierBelow}
                  onClick={() => addModifier("base")}
                >
                  <AddIcon className={styles.addIcon} />
                  <span style={{ color: "#2D8861", fontSize: "16px"}}>Add Modifier</span>
                </div>
              )}
            </div>
          </div>
        )
      });
    }

    // Elements
    elements.forEach((el, idx) => {
      items.push({
        id: el.id,
        operatorValue: operators[`op-${idx}`] || "＋",
        operatorOnChange: isReadOnly ? undefined : ((v) => setOperator(`op-${idx}`, v)),
        content: (
          <div className={styles.elementWrapper} style={{ marginTop: "30px", position: "relative", display: "flex", flexDirection: "column" }}>
            {!isReadOnly && (
              <div
                className={styles.elementDelete}
                onClick={() => removeElement(el.id)}
                title="Remove element"
              >
                <RemoveIcon width={14} height={14} />
              </div>
            )}

            {el.type === "chooser" && (
              <div className={styles.choiceBox} style={isReadOnly ? { pointerEvents: "none" } : undefined}>
                <button onClick={() => setElType(el.id, "value")}>Single Value</button>
                <span className={styles.orText}>OR</span>
                <button onClick={() => setElType(el.id, "linked")}>Link to Cell</button>
              </div>
            )}

            {el.type === "value" && (
              <div className={styles.valueBox}>
                <input
                  type="number"
                  placeholder="0.00"
                  value={el.value}
                  readOnly={isReadOnly}
                  onChange={(e) => !isReadOnly && setElValue(el.id, e.target.value)}
                />
              </div>
            )}

            {el.type !== "linked" && (
              <div className={styles.addModifierBelow} style={{ visibility: "hidden" }}>
                <AddIcon className={styles.addIcon} />
                <span>Add Modifier</span>
              </div>
            )}

            {el.type === "linked" && (
              <div className={styles.linkedWrapper} style={{ flexGrow: 1, width: "100%" }}>
                <div className={`${styles.formulaLeftSmall} ${isPrefilled ? styles.prefilledContainer : ""} ${modifiers[`el-${el.id}`]?.length > 0 ? styles.whiteBlock : ""}`} style={{ 
                  position: "relative",
                  width: "235px", 
                  maxWidth: "235px",
                  borderBottomLeftRadius: expandedModifiers[`el-${el.id}`] ? 0 : 10,
                  borderBottomRightRadius: expandedModifiers[`el-${el.id}`] ? 0 : 10,
                  borderBottomColor: expandedModifiers[`el-${el.id}`] ? "transparent" : "#c2e6d1"
                }}>
                  <LinkedCellBlock
                    key={`el-${el.id}-${activeCellKey}`}
                    elKey={`el-${el.id}`}
                    isFormula={true}
                    openSections={openSections}
                    setOpenSections={setOpenSections}
                    modifiers={modifiers}
                    addModifier={addModifier}
                    updateModifierOperator={updateModifierOperator}
                    updateModifierValue={updateModifierValue}
                    selections={selections}
                    setSelection={setSelection}
                    moduleOpts={MODULE_OPTS}
                    getSections={getSections}
                    getSubSections={getSubSections}
                    getCols={getCols}
                    getRows={getRows}
                    showLabels={false}
                    showAddModifier={false}
                    removeModifier={removeModifier}
                    updateModifierType={updateModifierType}
                    isPrefilled={isPrefilled}
                    expandedModifiers={expandedModifiers}
                    setExpandedModifiers={setExpandedModifiers}
                    isReadOnly={isReadOnly}
                  />
                </div>
                {!isReadOnly && (
                  <div
                    className={styles.addModifierBelow}
                    onClick={() => addModifier(`el-${el.id}`)}
                  >
                    <AddIcon className={styles.addIcon} />
                    <span style={{ color: "#2D8861", fontSize: "16px"}}>Add Modifier</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      });
    });
    
    // Add Element
    if (!isReadOnly) {
      const lastOpKey = elements.length === 0 ? "op-0" : "op-last";
      items.push({
        id: "add-btn",
        operatorValue: operators[lastOpKey] || "＋",
        operatorOnChange: (v) => setOperator(lastOpKey, v),
        content: (
          <div style={{ display: "flex", flexDirection: "column", marginTop: "30px" }}>
            <div className={styles.addElementBox} onClick={addElement}>
              <p><AddElementIcon style={{ marginTop: "-1px"}} /> Add Element</p>
            </div>
            <div className={styles.addModifierBelow} style={{ visibility: "hidden" }}>
              <AddIcon className={styles.addIcon} />
              <span>Add Modifier</span>
            </div>
          </div>
        )
      });
    }

    // 2. Chunk into rows of 3 items
    const rows = [];
    for (let i = 0; i < items.length; i += 3) {
      rows.push(items.slice(i, i + 3));
    }

    return (
      <div className={styles.formulaContainer} style={{ flexDirection: "column", flexWrap: "nowrap", alignItems: "flex-start" }}>
        {rows.map((row, rowIdx) => (
          <div key={rowIdx} style={{ display: "flex", flexDirection: "row", flexWrap: "nowrap", gap: "22px", width: "100%", alignItems: "stretch" }}>
            {row.map((item, colIdx) => {
              const globalIdx = rowIdx * 3 + colIdx;
              const hasNextItem = globalIdx + 1 < items.length;
              const nextItem = hasNextItem ? items[globalIdx + 1] : null;
              
              return (
                <React.Fragment key={item.id}>
                  {item.content}
                  
                  {hasNextItem && nextItem && (
                    <div style={{ display: "flex", alignItems: "center", marginTop: "30px", flexShrink: 0 }}>
                      <OperatorBtn
                        value={nextItem.operatorValue}
                        onChange={nextItem.operatorOnChange}
                      />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div ref={panelRef} className={styles.sidePanel} style={{ width: panelWidth }}>
      <div className={styles.panelHeader}  style= {{ fontSize: "18px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: "100%", boxSizing: "border-box" }}>
        <Tooltip
          content={`${isPrefilled ? (linkType === "formula" ? "Edit Formula Data" : "Edit Linked Data") : (linkType === "formula" ? "Formula Data" : "Link Data")}${selectedCell?.row && selectedCell?.col ? ` (${selectedCell.row} - ${formatColName(selectedCell.col)})` : ""}`}
          position="bottom"
          variant="black"
          style={{ flex: 1, minWidth: 0 }}
          tooltipStyle={{
            whiteSpace: "normal",
            width: "520px",
            wordBreak: "break-word",
            textAlign: "left",
            lineHeight: "1.6",
            padding: "8px 12px",
            fontSize: "13px",
          }}
        >
          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block", paddingRight: "10px", lineHeight: "1.4" }}>
            {isPrefilled ? (linkType === "formula" ? "Edit Formula Data" : "Edit Linked Data") : (linkType === "formula" ? "Formula Data" : "Link Data")}
            {selectedCell?.row && selectedCell?.col ? ` (${selectedCell.row} - ${formatColName(selectedCell.col)})` : ""}
          </span>
        </Tooltip>
        <span className={styles.close} onClick={handleClose} style={{ flexShrink: 0 }}>✕</span>
      </div>

      <div className={styles.radioRow} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "nowrap" }}>
        <div style={{ display: "flex", gap: "16px" }}>
          <label style={{ cursor: isReadOnly ? "default" : "pointer" }}>
            <input
              type="radio"
              name="sidePanelLinkType"
              style={{ marginTop: "2px" }}
              checked={linkType === "single"}
              disabled={isReadOnly}
              onChange={() => !isReadOnly && setLinkType("single")}
            />
            Single link
          </label>
          <label style={{ cursor: isReadOnly ? "default" : "pointer" }}>
            <input
              type="radio"
              style={{ marginTop: "2px" }}
              name="sidePanelLinkType"
              checked={linkType === "formula"}
              disabled={isReadOnly}
              onChange={() => !isReadOnly && setLinkType("formula")}
            />
            Formula
          </label>
        </div>

        {(linkType === "formula" || linkType === "single") && (
          <div style={{ display: "flex", alignItems: "center", gap: "16px", flexShrink: 0, marginLeft: "auto" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: isReadOnly ? "default" : "pointer", fontSize: "14px", fontWeight: "500", color: "#33584E" }}>
              <input
                type="checkbox"
                checked={isBold || isTotal || isSubtotal}
                disabled={isReadOnly}
                onChange={(e) => {
                  if (!isReadOnly && !isTotal && !isSubtotal) {
                    setIsBold(e.target.checked);
                  }
                }}
                style={{ accentColor: "#3AB67D", margin: 0, width: "16px", height: "16px" }}
              />
              Bold
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: isReadOnly ? "default" : "pointer", fontSize: "14px", fontWeight: "500", color: "#33584E" }}>
              <input
                type="checkbox"
                checked={isTotal}
                disabled={isReadOnly}
                onChange={(e) => !isReadOnly && handleTotalToggle(e.target.checked)}
                style={{ accentColor: "#3AB67D", margin: 0, width: "16px", height: "16px" }}
              />
              Total
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: isReadOnly ? "default" : "pointer", fontSize: "14px", fontWeight: "500", color: "#33584E" }}>
              <input
                type="checkbox"
                checked={isSubtotal}
                disabled={isReadOnly}
                onChange={(e) => !isReadOnly && handleSubtotalToggle(e.target.checked)}
                style={{ accentColor: "#3AB67D", margin: 0, width: "16px", height: "16px" }}
              />
              Subtotal
            </label>
          </div>
        )}
      </div>

      <div
        className={styles.panelBody}
        style={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
         // gap: "16px",
        }}
      >
        {isPanelDataLoading && (
          <div
            role="status"
            aria-label="Loading link data"
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255, 255, 255, 0.82)",
            }}
          >
            <Spinner animation="border" role="presentation" style={{ color: "#205a46" }} />
          </div>
        )}
        {linkType === "formula" && (
          <div style={{
            background: "#f8f9fa",
            border: "1px solid #CCD5D3",
            borderRadius: "10px",
            padding: "16px 20px",
            marginBottom: "0px",
            fontSize: "15px",
            color: "#475569",
            lineHeight: "1.6",
            whiteSpace: "normal",
            wordBreak: "break-word",
            fontWeight: "normal",
            width: "100%",
            boxSizing: "border-box"
          }}>
            Formula = {compileFormulaPreview()}
          </div>
        )}
        {linkType === "single" ? renderSingle() : renderFormula()}
      </div>

      <div className={styles.footer}>
      <button className={styles.cancelBtn} onClick={handleClose}>
        {isReadOnly ? "Close" : "Cancel"}
      </button>


      {!isReadOnly && (
        <div className={styles.rightActions}>
          {(isPrefilled || elements.length > 0 || selections["s-row"]) && (
            <button
              className={styles.deleteBtn}
              onClick={handleDeleteLink}
              disabled={isDeleting}
            >
              <DeleteIcon className={styles.deleteIcon} />
              <span>Delete Link</span>
            </button>
          )}

          <button
            className={styles.applyBtn}
            onClick={handleApply}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : isPrefilled ? "Update" : "Apply"}
          </button>
        </div>
      )}

      <DeletePopup
        isOpen={deleteConfirm !== null}
        title={deleteConfirm?.title}
        message={deleteConfirm?.message}
        onConfirm={deleteConfirm?.onConfirm}
        onCancel={() => setDeleteConfirm(null)}
      />
      <ErrorPopup
        isOpen={errorData !== null}
        title={errorData?.title}
        message={errorData?.message}
        onClose={() => setErrorData(null)}
      />
      <SuccessPopup
        isOpen={showSuccessPopup}
        message={successMessage}
        onClose={() => setShowSuccessPopup(false)}
      />
    </div>
    </div>
  );
};

export default SidePanel;
